import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios.js';
import './Register.css';

const Register = () => {
    const navigate = useNavigate();
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [firstname, setFirstname] = useState('');
    const [employeeSearchResults, setEmployeeSearchResults] = useState([]);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [error, setError] = useState('');
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [usernameError, setUsernameError] = useState('');

    // Validation function for username
    const validateUsername = (value) => {
        // Remove any spaces and non-alphanumeric characters
        const cleanValue = value.replace(/[^a-zA-Z0-9]/g, '');
        
        if (value !== cleanValue) {
            setUsernameError('Username can only contain letters and numbers (no spaces or special characters)');
        } else if (value.length < 3) {
            setUsernameError('Username must be at least 3 characters long');
        } else if (value.length > 20) {
            setUsernameError('Username must be less than 20 characters');
        } else {
            setUsernameError('');
        }
        
        return cleanValue;
    };

    // Handle username change with validation
    const handleUsernameChange = (e) => {
        const value = e.target.value;
        const cleanValue = validateUsername(value);
        setUsername(cleanValue);
    };

    // Debounce search
    useEffect(() => {
        const searchTimer = setTimeout(async () => {
            if (firstname.length >= 2) {
                try {
                    const response = await api.get(`/employees/search?name=${firstname}`);
                    setEmployeeSearchResults(response.data);
                } catch (err) {
                    console.error('Error searching employees:', err);
                    setEmployeeSearchResults([]);
                }
            } else {
                setEmployeeSearchResults([]);
            }
        }, 300);

        return () => clearTimeout(searchTimer);
    }, [firstname]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!selectedEmployee) {
            setError('Please select a valid employee name from the list');
            return;
        }

        // Validate username before submission
        if (usernameError || username.length < 3) {
            setError('Please fix the username validation errors');
            return;
        }

        console.log(selectedEmployee);
        try {
            const response = await api.post(`/register`, {
                username,
                password,
                employee_id: selectedEmployee.emp_id,
                firstname: selectedEmployee.name,
                lastname: selectedEmployee.lastname,
            });

            console.log('Registration successful:', response.data);
            setShowSuccessModal(true);
        } catch (err) {
            setError(err.response?.data?.message || 'Registration failed');
        }
    };

    const handleEmployeeSelect = (employee) => {
        setFirstname(employee.fullName);
        setSelectedEmployee(employee);
        setEmployeeSearchResults([]);
    };

    const handleConfirmSuccess = () => {
        setShowSuccessModal(false);
        navigate('/login');
    };

    return (
        <div className="register-container">
            <h2>ลงทะเบียน</h2>
            <form onSubmit={handleSubmit}>
                <div className="form-group">
                    <label>ชื่อพนักงาน:</label>
                    <input
                        placeholder="ค้นหาชื่อพนักงาน"
                        type="text"
                        value={firstname}
                        onChange={(e) => {
                            setFirstname(e.target.value);
                            setSelectedEmployee(null);
                        }}
                        required
                    />
                    {employeeSearchResults.length > 0 && (
                        <div className="search-results">
                            {employeeSearchResults.map((employee) => (
                                <div
                                    key={employee.id}
                                    className="search-result-item"
                                    onClick={() => handleEmployeeSelect(employee)}
                                >
                                    <div className="employee-name">
                                        {employee.fullName}
                                    </div>
                                    <div className="employee-details">
                                        <span>{employee.position}</span>
                                        <span>•</span>
                                        <span>{employee.team}</span>
                                        <span>•</span>
                                        <span>{employee.branch}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <div className="form-group">
                    <label>ชื่อผู้ใช้งาน:</label>
                    <input
                        type="text"
                        value={username}
                        onChange={handleUsernameChange}
                        placeholder="Enter username (letters and numbers only)"
                        required
                        style={{
                            borderColor: usernameError ? '#dc3545' : undefined
                        }}
                    />
                    {usernameError && (
                        <div style={{
                            color: '#dc3545',
                            fontSize: '0.875rem',
                            marginTop: '0.25rem'
                        }}>
                            {usernameError}
                        </div>
                    )}
                </div>
                <div className="form-group">
                    <label>รหัสผ่าน:</label>
                    <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                    />
                </div>
                {error && <div className="error-message">{error}</div>}
                <button type="submit">คลิกเพื่อลงทะเบียน</button>
            </form>

            {showSuccessModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h3>ลงทะเบียนสำเร็จ!</h3>
                        <div className="user-details-box">
                            <div className="user-detail">
                                <span className="detail-label">ชื่อ:</span>
                                <span className="detail-value">{selectedEmployee.fullName}</span>
                            </div>
                            <div className="user-detail">
                                <span className="detail-label">ตำแหน่ง:</span>
                                <span className="detail-value">{selectedEmployee.position}</span>
                            </div>
                            <div className="user-detail">
                                <span className="detail-label">ทีม:</span>
                                <span className="detail-value">{selectedEmployee.team}</span>
                            </div>
                            <div className="user-detail">
                                <span className="detail-label">สาขา:</span>
                                <span className="detail-value">{selectedEmployee.branch}</span>
                            </div>
                            <div className="user-detail">
                                <span className="detail-label">ชื่อผู้ใช้งาน:</span>
                                <span className="detail-value">{username}</span>
                            </div>
                        </div>
                        <p className="login-instruction">กรุณาใช้ข้อมูลนี้เพื่อเข้าสู่ระบบ</p>
                        <button class="to-login-button" onClick={handleConfirmSuccess}>คลิกเพื่อเข้าสู่ระบบ</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Register;